// Per-bucket occurrence bars. Ported from MedicalAssistant CountChart.tsx:
// bars live in one shared flex row so a taller date label cannot shrink an
// individual bar and make identical counts look different heights.

interface Props {
  days: { date: string; label?: string; count: number }[];
  valueFormat?: (n: number) => string;
  valueUnit?: string;
  emptyHint?: string;
}

export function CountChart({
  days,
  valueFormat = String,
  valueUnit,
  emptyHint = "No entries in this range.",
}: Props) {
  if (days.length === 0 || days.every((d) => d.count === 0)) {
    return (
      <p className="mt-6 text-center text-sm text-gray-400 dark:text-gray-500">{emptyHint}</p>
    );
  }

  const max = Math.max(...days.map((d) => d.count), 1);
  const n = days.length;
  const dense = n > 7;
  const labelClass = n <= 31 ? "text-[10px]" : "text-[8px]";

  return (
    <div className="w-full">
      <div className="flex h-40 w-full items-stretch gap-[2px]">
        {days.map((d) => {
          const pct = max > 0 ? (d.count / max) * 100 : 0;
          const date = new Date(d.date + "T00:00:00Z");
          const fullLabel =
            d.label ??
            date.toLocaleDateString(undefined, {
              weekday: "short",
              month: "short",
              day: "numeric",
              timeZone: "UTC",
            });
          return (
            <div
              key={d.date}
              className="flex min-w-0 flex-1 items-end"
              title={`${fullLabel}: ${valueFormat(d.count)}${valueUnit ? " " + valueUnit : ""}`}
            >
              <div
                className={
                  "w-full rounded-t-md transition-all " +
                  (d.count > 0 ? "bg-(--accent)" : "bg-gray-100 dark:bg-gray-800")
                }
                style={{ height: `${Math.max(pct, d.count > 0 ? 4 : 0)}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className={`mt-1.5 flex w-full gap-[2px] ${dense ? "h-10" : "h-4"}`}>
        {days.map((d) => {
          const date = new Date(d.date + "T00:00:00Z");
          const dayLabel =
            d.label ??
            (n <= 7
              ? date.toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                  timeZone: "UTC",
                })
              : `${date.getUTCMonth() + 1}/${date.getUTCDate()}`);
          return (
            <div key={d.date} className="flex min-w-0 flex-1 items-start justify-center overflow-hidden">
              <span
                className={`${labelClass} shrink-0 text-gray-400 dark:text-gray-500 ${dense ? "" : "whitespace-nowrap"}`}
                style={dense ? { writingMode: "vertical-rl", textOrientation: "sideways" } : undefined}
              >
                {dayLabel}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function TrendChart({
  points,
  unit,
  emptyHint = "No numeric values in this range.",
}: {
  points: { date: string; value: number }[];
  unit?: string;
  emptyHint?: string;
}) {
  if (points.length === 0) {
    return <p className="mt-6 text-center text-sm text-gray-400 dark:text-gray-500">{emptyHint}</p>;
  }

  const width = 640;
  const height = 180;
  const padL = 36;
  const padR = 12;
  const padT = 12;
  const padB = 28;
  const ys = points.map((p) => p.value);
  const min = Math.min(...ys);
  const max = Math.max(...ys);
  const span = max - min || 1;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;

  const xy = points.map((p, i) => {
    const x = padL + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
    const y = padT + innerH - ((p.value - min) / span) * innerH;
    return { ...p, x, y };
  });
  const d = xy.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label="Trend">
      <line
        x1={padL}
        y1={padT}
        x2={padL}
        y2={height - padB}
        className="stroke-gray-200 dark:stroke-gray-700"
        strokeWidth="1"
      />
      <line
        x1={padL}
        y1={height - padB}
        x2={width - padR}
        y2={height - padB}
        className="stroke-gray-200 dark:stroke-gray-700"
        strokeWidth="1"
      />
      <text x={4} y={padT + 4} className="fill-gray-400 text-[10px]">
        {max.toLocaleString(undefined, { maximumFractionDigits: 2 })}
        {unit ? ` ${unit}` : ""}
      </text>
      <text x={4} y={height - padB} className="fill-gray-400 text-[10px]">
        {min.toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </text>
      <path d={d} fill="none" className="stroke-(--accent)" strokeWidth="2" />
      {xy.map((p) => (
        <circle
          key={p.date + String(p.x)}
          cx={p.x}
          cy={p.y}
          r="3"
          className="fill-(--accent)"
        >
          <title>
            {p.date}: {p.value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            {unit ? ` ${unit}` : ""}
          </title>
        </circle>
      ))}
    </svg>
  );
}
